import * as React from "react";
import { observer } from "mobx-react-lite";
import { Mk8sDraftMobx } from "../../../../../mst/stores/mk8s.draft";
import { homeLink, request } from "../../../../../services/cpln";
import { DryRunAlert } from "../../../dryRunAlert";
import { mk8sDryRun } from "../../../../../mobxStores/dryRun/mk8s";
import { NGSelect } from "../../../../../newcomponents/select/ngselect";
import { NGFormElement } from "../../../../../newcomponents/ngformelement/ngformelement";
import { MoreHorizontal, PlusCircle } from "react-feather";
import { useNGFormContext } from "../../../../../reactContexts/ngFormContext";
import { NGProviderBrowser } from "../../../ngProviderBrowser";
import { NGFormLabel } from "../../../../../newcomponents/text/formLabel";
import { NGKindSelect } from "../../../../../newcomponents/select/ngkindselect";
import NGAlert from "../../../../../newcomponents/alert";
import { NGProviderCreateGCPSecret } from "../../../ngProviderCreateGcpSecret";
import { NGInput } from "../../../../../newcomponents/input/input";
import { NGRadioGroup } from "../../../../../newcomponents/radioGroup";
import { ProviderGCPInstructions } from "../../../provider/gcp/instructions";
import { ExternalLink } from "../../../../../newcomponents/table/components/ExternalLink";
import { DOCS_URL } from "../../../../../envVariables";

interface Props {
  mk8sDraft: Mk8sDraftMobx;
}
const Mk8sCreateProviderGCPRaw: React.FC<Props> = ({ mk8sDraft }) => {
  const g = mk8sDraft.provider_gcp!;
  const formData = useNGFormContext();

  const [recommendedOptions, setRecommendedOptions] = React.useState<string[]>([]);
  React.useEffect(() => {
    fetchRecommendedOptions();
  }, []);
  async function fetchRecommendedOptions() {
    try {
      const { data: schemaRes } = await request({ url: "/catalog/schema/mk8s" });
      const images: string[] = schemaRes.keys.spec.keys.provider.keys.gcp.keys.image.keys.recommended.allow;
      setRecommendedOptions(images);
    } catch (e) {
      setRecommendedOptions([]);
    }
  }

  const [browserKey, setBrowserKey] = React.useState("");

  return (
    <>
      <div>
        <div className="mb-4 flex items-baseline gap-1">
          <ExternalLink label="Click here" url={`${DOCS_URL}/mk8s/gcp`} disableCopy />
          <span>to learn more about GCP Provider in docs</span>
        </div>
        <div className="mb-4">
          <NGFormLabel required invalid={!g.saKeyLink}>
            GCP Service Account Key Secret
          </NGFormLabel>
          <ProviderGCPInstructions draft={mk8sDraft} />
          <div className="mb-2 flex items-start gap-2">
            <NGKindSelect
              name={"gcp.saKeyName"}
              style={{ width: 450 }}
              value={g.saKeyLink}
              invalid={!g.saKeyLink}
              onChange={(value) => g.setSaKeyName(value)}
              kind={"secret"}
              queries={[{ property: "type", value: "gcp" }]}
              buttons={[
                {
                  title: "Create",
                  render: () => <PlusCircle className="h-4" />,
                  onClick: () => setBrowserKey("createSecret"),
                },
              ]}
            />
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get("gcp.saKeyName").touched}
              dryRunResponse={mk8sDryRun.response}
              path={"spec.provider.gcp.saKeyLink"}
            />
          </div>
          <NGAlert
            style={{ width: 450 }}
            render={() => (
              <div className="flex flex-col text-sm">
                <span>A GCP secret that contains your GCP service account key.</span>
                <span>It will be used to manage servers in your GCP account.</span>
              </div>
            )}
          />
        </div>
        <div className="flex gap-2 items-start">
          <NGFormElement
            name={"gcp.projectId"}
            label={g.projectId.label}
            value={g.projectId.value}
            onChange={g.projectId.setValue}
            required={g.projectId.isRequired}
            error={g.projectId.error}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get("gcp.projectId").touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={g.projectId.setValue}
              path={"spec.provider.gcp.projectId"}
            />
          </div>
        </div>
        <div className="flex gap-2 items-start">
          <NGFormElement
            name={"gcp.region"}
            label={g.region.label}
            value={g.region.value}
            onChange={g.region.setValue}
            required={g.region.isRequired}
            error={g.region.error}
            innerButtons={[
              {
                title: "Browse",
                onClick: () => {
                  setBrowserKey("region");
                  formData.set("gcp.region", { touched: true });
                },
                render: () => <MoreHorizontal />,
              },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get("gcp.region").touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={g.region.setValue}
              path={"spec.provider.gcp.region"}
            />
          </div>
        </div>
        <div className="flex items-center gap-1">
          <NGFormLabel name={"gcp.image.recommended"} required invalid={!g.image.recommended}>
            Image
          </NGFormLabel>
        </div>
        <NGRadioGroup
          value={g.image.type}
          onChange={(value) => g.image.setType(value as any)}
          options={[
            { label: "Recommended", value: "recommended" },
            { label: "Family", value: "family" },
            { label: "Exact", value: "exact" },
          ]}
        />
        {g.image.type === "recommended" ? (
          <div className="mb-4 flex items-start gap-2">
            <NGSelect
              name={`gcp.image.recommended`}
              style={{ width: 450, minWidth: 450 }}
              value={g.image.recommended}
              invalid={!g.image.recommended}
              onChange={g.image.setRecommended}
              options={recommendedOptions.map((v) => ({ label: v, value: v }))}
            />
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={
                formData.get("gcp.image.recommended").touched ||
                formData.get("gcp.image.exact").touched ||
                formData.get("gcp.image.familyProject").touched ||
                formData.get("gcp.image.familyFamily").touched
              }
              dryRunResponse={mk8sDryRun.response}
              path={`spec.provider.gcp.image`}
              paths={[`spec.provider.gcp.image.recommended`]}
            />
          </div>
        ) : null}
        {g.image.type === "exact" ? (
          <div className="mb-4 flex items-start gap-2">
            <NGInput
              name={`gcp.image.exact`}
              style={{ width: 450, minWidth: 450 }}
              value={g.image.exact}
              invalid={!g.image.exact}
              onChange={(e) => g.image.setExact(e.target.value)}
            />
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={
                formData.get("gcp.image.recommended").touched ||
                formData.get("gcp.image.exact").touched ||
                formData.get("gcp.image.familyProject").touched ||
                formData.get("gcp.image.familyFamily").touched
              }
              dryRunResponse={mk8sDryRun.response}
              path={`spec.provider.gcp.image`}
              paths={[`spec.provider.gcp.image.exact`]}
            />
          </div>
        ) : null}
        {g.image.type === "family" ? (
          <>
            <NGFormLabel name={"gcp.image.familyProject"} required invalid={!g.image.familyProject}>
              Family | Project
            </NGFormLabel>
            <div className="mb-4 flex items-start gap-2">
              <NGInput
                name={`gcp.image.familyProject`}
                style={{ width: 450, minWidth: 450 }}
                value={g.image.familyProject}
                invalid={!g.image.familyProject}
                onChange={(e) => g.image.setFamilyProject(e.target.value)}
              />
              <DryRunAlert
                key={mk8sDryRun.key}
                canShow={
                  formData.get("gcp.image.recommended").touched ||
                  formData.get("gcp.image.exact").touched ||
                  formData.get("gcp.image.familyProject").touched ||
                  formData.get("gcp.image.familyFamily").touched
                }
                dryRunResponse={mk8sDryRun.response}
                path={`spec.provider.gcp.image`}
                paths={[`spec.provider.gcp.image.family`, `spec.provider.gcp.image.family.project`]}
              />
            </div>
            <NGFormLabel name={"gcp.image.familyFamily"} required invalid={!g.image.familyFamily}>
              Family | Family
            </NGFormLabel>
            <div className="mb-4 flex items-start gap-2">
              <NGInput
                name={`gcp.image.familyFamily`}
                style={{ width: 450, minWidth: 450 }}
                value={g.image.familyFamily}
                invalid={!g.image.familyFamily}
                onChange={(e) => g.image.setFamilyFamily(e.target.value)}
              />
              <DryRunAlert
                key={mk8sDryRun.key}
                canShow={
                  formData.get("gcp.image.recommended").touched ||
                  formData.get("gcp.image.exact").touched ||
                  formData.get("gcp.image.familyProject").touched ||
                  formData.get("gcp.image.familyFamily").touched
                }
                dryRunResponse={mk8sDryRun.response}
                path={`spec.provider.gcp.image`}
                paths={[`spec.provider.gcp.image.family`, `spec.provider.gcp.image.family.family`]}
              />
            </div>
          </>
        ) : null}

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={"gcp.network"}
            label={g.network.label}
            value={g.network.value}
            onChange={g.network.setValue}
            error={g.network.error}
            required={g.network.isRequired}
            innerButtons={[
              {
                title: "Browse",
                onClick: () => {
                  setBrowserKey("networks");
                  formData.set("gcp.network", { touched: true });
                },
                render: () => <MoreHorizontal />,
              },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get("gcp.network").touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={g.network.setValue}
              path={"spec.provider.gcp.network"}
            />
          </div>
        </div>
      </div>
      {browserKey === "createSecret" ? (
        <NGProviderCreateGCPSecret
          onOk={(secretName: string) => g.setSaKeyName(secretName)}
          onClose={() => setBrowserKey("")}
          title={"Create GCP Secret"}
          dataLabel={"GCP Service Account Key"}
        />
      ) : null}
      {browserKey === "region" ? (
        <NGProviderBrowser
          title="Browse GCP Regions"
          initialValue={g.region.value}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "regions",
                cluster: mk8sDraft.asObject,
              },
            });
            console.log(`region discovery`);
            console.log(data);
            return data.regions;
          }}
          onOk={(value) => g.region.setValue(value as string)}
          onClose={() => setBrowserKey("")}
          labels={["Name"]}
          getData={(item: any) => [item, item]}
        />
      ) : null}
      {browserKey === "networks" ? (
        <NGProviderBrowser
          title="Browse GCP Networks"
          initialValue={g.network.value}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "networks",
                cluster: mk8sDraft.asObject,
              },
            });
            // {name: string}
            return data;
          }}
          onOk={(value) => g.network.setValue(value as string)}
          onClose={() => setBrowserKey("")}
          labels={["Name"]}
          getData={(item: any) => {
            return [item.name, item.name];
          }}
        />
      ) : null}
    </>
  );
};

export const Mk8sCreateProviderGCP = observer(Mk8sCreateProviderGCPRaw);
